/**
 * Video load policy — only decides "when to start downloading", never touches playback.
 *
 * Why it's needed (measured 2026-08-07):
 * The four media snippets soulog-sticky-steps / feature-reveal / showcase-scroll / expertise-scroll
 * originally output `<video autoplay preload="metadata">` + a real src. **With autoplay present,
 * preload="metadata" doesn't count** — to autoplay, the browser must buffer until playable. The result:
 * on /pages/for-life, 8 videos all started downloading at 3056ms, 5.2MB in total, before the user scrolled at all;
 * the hero's LCP image was pushed out to 9.5s. (Re-running with --disable-full-page-screenshot still gave 8 / 4755KB,
 * ruling out interference from Lighthouse scrolling the page itself.)
 *
 * Those four sections' JS do have an IntersectionObserver, but it only calls play()/pause()
 * and never touches src — that's **playback control**, not load control. A later pause() from JS can't stop
 * requests that have already been sent. So loading has to be managed separately, which is what this file does.
 *
 * Policy (deliberately optimizes mobile only; desktop stays as it was):
 *   · ≥768px: raise preload + load() as soon as the script runs, equivalent to the original autoplay behavior.
 *     Desktop bandwidth is ample and it already measures 78~87 points; a small gain isn't worth the risk of "scrolled to the video before it finished downloading".
 *   · <768px: IntersectionObserver starts downloading 150% of the viewport height ahead.
 *     Basis for 150% (measured, not estimated): Lighthouse's mobile preset viewport is 412×823, and on for-life
 *     the content after the hero starts at y≈3387. rootMargin 150% = 1234px, so at scrollY=0 the IO's detection
 *     upper bound is 823+1234 = 2057 < 3387 — out of reach, so no video downloads during the LCP phase,
 *     all bandwidth goes to the hero image; once the user starts scrolling there's still about 2.5 screens of lead time.
 *     ⚠️ Don't derive this value from element coordinates in a desktop viewport: at 412px wide the layout reflows into a single column, and element positions
 *     are completely different from those at 1456px wide (my first version was computed from the desktop 2265px, and was wrong).
 *     Before changing this number, re-measure in the target viewport and re-run the "How to verify" section below.
 *
 * How to verify (should be run after changing this file or any of those four snippets):
 *   npx lighthouse@12 "<page URL>" --quiet --output=json --output-path=/tmp/a.json \
 *     --only-categories=performance --chrome-flags="--headless=new --no-sandbox"
 *   Then count the entries in network-requests with resourceType == 'Media':
 *     mobile (without --preset) should be 0; desktop (--preset=desktop) should match pre-change.
 *   2026-08-07 baseline: for-life mobile 0 / 0 KB (before the change: 8 / 5222 KB);
 *                   desktop 8 / 5222 KB (same as before the change).
 *
 * Playback is still managed by each section's own IO; this file doesn't interfere:
 *   sticky-steps setupMobile() threshold .35 → play(); expertise playAll();
 *   showcase IO → play(); feature-reveal reveal() → play().
 *   play() itself triggers loading, so even if the user scrolls faster than 150% nothing is missed,
 *   it just degrades to "play while downloading" — for the poster fallback, see the snippet side.
 *
 * Why the breakpoint is 768 rather than each section's own value: those three breakpoints all differ
 * (sticky-steps 750 / expertise 1024 / showcase 1024+hover), so there's no common value to align to.
 * The load policy only cares "is this a bandwidth-constrained narrow screen", unrelated to each layout's boundary,
 * so it uses the repo's main breakpoint 768 (see the responsive guidelines in CLAUDE.md), which is also the most conservative tier.
 */
(function () {
	"use strict";

	// The marker is a class rather than a data-* attribute: three snippets go through Shopify's `video_tag` filter,
	// and Liquid parameter names can't contain hyphens, so `data-lazy-video:` is a syntax error —
	// `class`, on the other hand, is a parameter video_tag natively supports, and is the only marker that covers both the video_tag branch and
	// the hand-written <video> branch.
	// When the four sections are on the same page each includes this file once — the browser downloads it once but executes it multiple times;
	// this flag ensures only one set of observers is created.
	if (window.__soulogVideoLoadPolicy) return;
	window.__soulogVideoLoadPolicy = true;

	var SELECTOR = "video.soulog-lazy-video";
	var MOBILE_MQ = "(max-width: 767px)";
	var ROOT_MARGIN = "150%";

	/**
	 * Start downloading. Must be idempotent — load() resets currentTime to zero,
	 * and calling it again on a video that's already playing makes the picture jump back to the start.
	 */
	function startLoad(video) {
		if (!video || video.dataset.videoLoadStarted) return;
		video.dataset.videoLoadStarted = "1";
		video.preload = "auto";
		// readyState 0 = no data received yet; only then is load() necessary;
		// if data exists, something else (most likely play()) has already triggered loading, and another load() would only interrupt it.
		if (video.readyState === 0) video.load();
	}

	var io = null;

	function observer() {
		if (io || !("IntersectionObserver" in window)) return io;
		io = new IntersectionObserver(
			function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (!entries[i].isIntersecting) continue;
					startLoad(entries[i].target);
					io.unobserve(entries[i].target);
				}
			},
			{ rootMargin: ROOT_MARGIN },
		);
		return io;
	}

	function apply(root) {
		var scope = root && root.querySelectorAll ? root : document;
		var videos = scope.querySelectorAll(SELECTOR);
		if (!videos.length) return;

		// Desktop: load everything immediately, behaving the same as the pre-change autoplay.
		// Old browsers without IntersectionObserver take this path too — better to over-download than to fail to play.
		var lazy = window.matchMedia && window.matchMedia(MOBILE_MQ).matches && "IntersectionObserver" in window;
		if (!lazy) {
			for (var i = 0; i < videos.length; i++) startLoad(videos[i]);
			return;
		}

		var ob = observer();
		for (var j = 0; j < videos.length; j++) {
			if (videos[j].dataset.videoLoadStarted) continue;
			ob.observe(videos[j]);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			apply(document);
		});
	} else {
		apply(document);
	}

	// Adding/removing sections in the theme editor re-mounts the DOM; newly arrived videos need to be taken over too.
	document.addEventListener("shopify:section:load", function (e) {
		apply(e.target || document);
	});
})();
